/**
 * Config-driven port of v1's CaptureService.processCaptureWorkflow. Builds the CustomEftDto the local
 * Device Service expects at POST /captures/eft (AniJson Type-1/Type-2 records + Type-4 fingerprint
 * images) from a Site-Admin-editable WorkflowConfig: static fields, demographic→EFT field map, TCN
 * template, and post rules.
 *
 * DTO shape: device-service CustomEftDTO { subscriptionItemId, originalTxId, transaction }.
 */
import type { WorkflowConfig, WorkflowFaceConfig } from './config';
import { formatEbtsRef, tryParseEbtsRef } from './ebtsRef';
import { runRules } from './rules';
import type { DeviceInfo } from '../deviceService/client';
import { DEFAULT_DATE_FORMAT, formatDateValue } from './dateFormat';
import { ampEntries, imagesToClear } from './amputations';

type FieldVal = string | number;
export interface AniRecord { type: string; idx: number; fields: Record<string, FieldVal>; img?: string }
export interface CustomEftDto {
  subscriptionItemId?: string;
  originalTxId?: string;
  transaction: { idcPadded: boolean; records: AniRecord[] };
}
type Demo = Record<string, string>;

// FD-258 scan positions → EBTS finger position (FGP) codes.
// Exported so the Ani cache can file an impression under the same position the EFT will put it in;
// a second copy of this table would be a silent mismatch between what is staged and what is built.
export const FGP: Record<string, number> = {
  ROLLED_RIGHT_THUMB: 1, ROLLED_RIGHT_INDEX: 2, ROLLED_RIGHT_MIDDLE: 3, ROLLED_RIGHT_RING: 4, ROLLED_RIGHT_LITTLE: 5,
  ROLLED_LEFT_THUMB: 6, ROLLED_LEFT_INDEX: 7, ROLLED_LEFT_MIDDLE: 8, ROLLED_LEFT_RING: 9, ROLLED_LEFT_LITTLE: 10,
  FLAT_RIGHT_THUMB: 11, FLAT_LEFT_THUMB: 12, RIGHT_FOUR: 13, LEFT_FOUR: 14,
};
const PLAIN_FGPS = new Set([11, 12, 13, 14, 15]);

const padNum = (n: number, digits: number) => String(n).padStart(digits, '0');
function formatDate(date: Date): string {
  return date.getFullYear().toString() + padNum(date.getMonth() + 1, 2) + padNum(date.getDate(), 2);
}
/**
 * The operator's current LOCAL calendar date as YYYYMMDD, for T1_DAT (1.005) and dates-printed. Uses
 * local date parts only — never UTC — so the transaction date is the day the operator actually sees,
 * with no timezone shift (e.g. a late-evening capture doesn't roll to tomorrow's UTC date).
 */
function todayYmd(): string {
  return formatDate(new Date());
}
function formatDateTime(date: Date): string {
  let s = (date.getUTCFullYear() % 100).toString();
  const start = new Date(date.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((date.getTime() - start.getTime()) / 86_400_000);
  s += padNum(dayOfYear, 3) + padNum(date.getUTCHours(), 2) + padNum(date.getUTCMinutes(), 2) + padNum(date.getUTCSeconds(), 2);
  return s;
}
function buildName(first?: string, middle?: string, last?: string, suffix?: string): string {
  let name = `${(last ?? '').trim()},${(first ?? '').trim()}`;
  if (middle && middle.length > 0) name += ` ${middle.trim().charAt(0)}`;
  if (suffix && suffix.length > 0) name += ` ${suffix}`;
  return name.toUpperCase();
}

/**
 * The inverse of {@link buildName}, for reading an imported EFT back onto the form.
 *
 * T2_NAM (2.018) is COMPOSED — `postRules.setName` writes `LAST,FIRST M SUFFIX` from four separate
 * inputs — so there is no entry in a workflow's fieldMap to reverse it, and an import that only walked
 * that map dropped the subject's name entirely.
 *
 * Middle is written as a single initial and a suffix as a short token, which is what makes them
 * separable on the way back: after the first name, a lone character is the middle initial and anything
 * longer is the suffix. Ambiguity is resolved in favour of the middle initial because that is what
 * buildName actually emits.
 */
/**
 * EBTS date (YYYYMMDD) back to the `YYYY-MM-DD` an `<input type="date">` requires.
 *
 * The outbound direction is one line in buildCustomEft — every non-digit is stripped and the value
 * truncated to eight characters — so an import that copied 2.022 across verbatim put `20050316` into a
 * date input, which silently renders as empty. Returns the input unchanged when it is not eight digits,
 * so a field that already holds a real date, or something unexpected, is left alone rather than mangled.
 */
export function parseEbtsDate(value: string): string {
  const digits = (value ?? '').trim();
  if (!/^\d{8}$/.test(digits)) return value;
  const [y, m, d] = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)];
  // A structurally valid string that is not a real date (month 00, day 32) would be rejected by the
  // input anyway; leaving it visible is better than converting it into a plausible wrong date.
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return value;
  return `${y}-${m}-${d}`;
}

/**
 * Generational suffixes, mapped to the values the form's Suffix select actually offers.
 *
 * Recognising the SUFFIX is what makes the rest of the name unambiguous — everything between the first
 * name and a suffix is the middle name, however long it is. The previous rule went the other way round
 * (a single character is the middle initial, anything else is suffix), which read `WILSON,AARON ISIAH JR`
 * as a surname, a first name and a two-word suffix of "ISIAH JR", losing the middle name entirely.
 *
 * Normalised to the option values because the parsed suffix populates a `<select>`: `JR.` with the
 * trailing dot, or a lower-case `Jr`, is not any option and lands as blank — the field silently empties
 * itself and the operator has no way to know the file said otherwise.
 */
const SUFFIXES: Record<string, string> = {
  JR: 'JR', SR: 'SR',
  II: 'II', III: 'III', IV: 'IV', V: 'V',
  '2ND': 'II', '3RD': 'III', '4TH': 'IV', '5TH': 'V',
};

/**
 * Professional suffixes. Not offered by the Suffix select, but they DO appear after a generational one
 * (`SMITH,JOHN Q III ESQ`), and a token-by-token rule that stops at the first unknown word would read
 * them as part of the middle name.
 */
const PROFESSIONAL = new Set(['ESQ', 'MD', 'PHD', 'DDS', 'DVM', 'CPA', 'RN', 'DO']);

/** A token's canonical suffix value, or null when it is not a suffix. Dots and case are not meaningful. */
function asSuffix(token: string): string | null {
  const bare = token.replace(/\./g, '').toUpperCase();
  return SUFFIXES[bare] ?? (PROFESSIONAL.has(bare) ? bare : null);
}

export function parseName(nam: string): { firstName?: string; middleName?: string; lastName?: string; suffix?: string } {
  const value = (nam ?? '').trim();
  if (!value) return {};

  const comma = value.indexOf(',');
  // No comma at all: not the composed form. Treated as a surname rather than guessed at.
  if (comma < 0) return { lastName: value };

  const lastName = value.slice(0, comma).trim();
  const rest = value.slice(comma + 1).trim();
  if (!rest) return lastName ? { lastName } : {};

  const [firstName, ...tail] = rest.split(/\s+/);
  const out: { firstName?: string; middleName?: string; lastName?: string; suffix?: string } = {};
  if (lastName) out.lastName = lastName;
  if (firstName) out.firstName = firstName;

  /*
   * Take the suffix off the END first, consuming a RUN of suffix tokens rather than a single one —
   * `III ESQ` is one suffix in two words. The run stops at the first token that is not a recognised
   * suffix, so a middle name that happens to collide with a suffix word (`SMITH,JOHN VERA`) keeps its
   * place, while `SMITH,JOHN V` still resolves the trailing V as generational, which in that position it
   * almost always is.
   */
  const parts = [...tail];
  const suffixTokens: string[] = [];
  for (let i = parts.length - 1; i >= 0; i--) {
    const suffix = asSuffix(parts[i]!);
    if (!suffix) break;
    suffixTokens.unshift(suffix);
    parts.pop();
  }
  if (suffixTokens.length) out.suffix = suffixTokens.join(' ');

  /*
   * Everything left between the first name and the suffix is the middle name — one initial, several, or a
   * full name. Dots are dropped from a lone initial (`I.` -> `I`) because that is the form buildName
   * emits, but a spelled-out middle name is preserved as written.
   */
  if (parts.length) {
    const middle = parts.join(' ');
    out.middleName = parts.length === 1 && middle.replace(/\./g, '').length === 1
      ? middle.replace(/\./g, '')
      : middle;
  }
  return out;
}

function applyTransform(value: string, transform?: string): string {
  switch (transform) {
    /*
     * Everything that is not a letter or a digit.
     *
     * `nodots` was the SWFT TCN's sanitiser and it only ever removed dots, because the serials in front of
     * anyone at the time looked like `005022636.G2022`. A scanner reporting `FS-369-2` then put hyphens
     * straight into the TCN — `55R51#FS-369-2)` instead of `55R51#FS3692)`. The literal `#` and `)` in the
     * template are ours and deliberate; what a device reports is not, so it is the VALUE that gets
     * cleaned, not the rendered TCN.
     */
    case 'alnum': return value.replace(/[^A-Za-z0-9]/g, '');
    /** Kept because a stored template may still name it. `alnum` is what a serial wants. */
    case 'nodots': return value.replaceAll('.', '');
    case 'upper': return value.toUpperCase();
    case 'lower': return value.toLowerCase();
    default: return value;
  }
}

/**
 * Render a TCN template. Tokens:
 *  {ori} {tot} {spec} {date} {datetime}
 *  {rand:N}          → N random digits
 *  {fill:TOTAL}      → random digits padding the whole TCN to TOTAL chars
 *  {field:NAME}      → demographic value, optional |alnum |nodots |upper |lower
 *  {?NAME:suffix}    → value+suffix iff NAME is present, else empty
 */
/**
 * Placeholder for the {fill:N} slot while the rest of the TCN is rendered, replaced with digits at the end.
 *
 * A private-use code point rather than NUL: it cannot appear in a real TCN either way, but a source file that
 * contains a NUL byte is classified as BINARY, and the project's grep skips binary files without saying so — this
 * file was invisible to every code search until someone noticed.
 */
const FILL_MARK = '\uE000';

export function renderTcn(template: string, ctx: { ori: string; tot: string; spec: string; demo: Demo }): string {
  const now = new Date();
  let fillTo: number | null = null;

  const rendered = template.replace(/\{([^}]+)\}/g, (_m, token: string) => {
    if (token === 'ori') return ctx.ori;
    if (token === 'tot') return ctx.tot;
    if (token === 'spec') return ctx.spec;
    if (token === 'date') return formatDate(now);
    if (token === 'datetime') return formatDateTime(now);
    if (token.startsWith('rand:')) return padNum(Math.round(Math.random() * 1e8), Number(token.slice(5)) || 5);
    if (token.startsWith('fill:')) { fillTo = Number(token.slice(5)) || null; return FILL_MARK; } // placeholder
    if (token.startsWith('field:')) {
      const parts = token.slice(6).split('|');
      const name = parts[0] ?? '';
      return applyTransform(ctx.demo[name] ?? '', parts[1]);
    }
    if (token.startsWith('?')) {
      const rest = token.slice(1);
      const idx = rest.indexOf(':');
      const name = idx >= 0 ? rest.slice(0, idx) : rest;
      const suffix = idx >= 0 ? rest.slice(idx + 1) : '';
      return ctx.demo[name] ? `${ctx.demo[name]}${suffix}` : '';
    }
    return '';
  });

  if (fillTo != null) {
    const base = rendered.replace(FILL_MARK, '');
    const need = Math.max(0, fillTo - base.length);
    const digits = need > 0 ? padNum(Math.round(Math.random() * 10 ** Math.min(need, 15)), need).slice(0, need) : '';
    return rendered.replace(FILL_MARK, digits);
  }
  return rendered;
}

/**
 * Type-10 facial record, when the workflow puts the photograph in the EFT.
 *
 * Only the fields AniJson cannot derive are set here. Importing the image populates the geometry —
 * HLL, VLL, BPX, CGA and CSP — from the image itself, so writing them by hand would only create a
 * second source of truth that can disagree with the pixels.
 *
 * ATF does not use this: its photograph goes to the ATF portal separately and never enters the EFT.
 */
function addFace(records: AniRecord[], face: WorkflowFaceConfig, photo: string, ori: string, captureDate: string) {
  if (!face.targets.includes('type10') || !photo) return;
  const t10: AniRecord = { type: 'TYPE_10', idx: 1, img: photo, fields: {} };
  t10.fields['3'] = 'FACE';        // IMT — image type
  t10.fields['4'] = ori;           // SRC — the agency that captured it
  t10.fields['5'] = captureDate;   // PHD — photo date, YYYYMMDD
  // SAP describes how the image was acquired and how much can be relied on. 13 is the usual level for
  // a frontal photograph taken for identification; a workflow can override it.
  t10.fields['13'] = String(face.sap ?? 13);
  t10.fields['20'] = face.pose ?? 'F'; // POS — 'F' is full-face frontal
  records.push(t10);
}

function addFingerprints(records: AniRecord[], t2: AniRecord, prints: Record<string, string>, ampCodes: Record<string, string>) {
  let i = 1;
  let amp = 1;
  /*
   * An AMP entry and an image of the same finger contradict each other — the submission says the finger
   * is missing and then encloses a picture of it. Enforced HERE as well as in the capture screen, because
   * a resumed draft or an imported file can carry an impression the operator has since marked.
   */
  const suppressed = imagesToClear(ampCodes);
  for (const [scanType, data] of Object.entries(prints)) {
    const fgp = FGP[scanType];
    if (!fgp || !data) continue;
    if (suppressed.has(scanType)) continue;
    const t4: AniRecord = { type: 'TYPE_04', idx: i++, img: data, fields: {} };
    /*
     * T4_IMP is a ONE-BYTE field (`4.003 T4_IMP B1 1 1`), mapped 0 = live-scan plain, 1 = live-scan
     * rolled. This wrote '00'/'01', two characters, which EBTS validation flags as both too long and
     * unmapped. The Device Service's own writer happens to narrow it to a single byte on the way out —
     * a real accepted submission carries 0x30/0x31 — so submissions were unaffected, but the value we
     * hand over should be the one the specification actually names.
     */
    t4.fields['3'] = PLAIN_FGPS.has(fgp) ? '0' : '1';
    t4.fields['4'] = fgp;
    t4.fields['8'] = 1;
    records.push(t4);
  }
  /*
   * 2.084 names INDIVIDUAL fingers, so a marked plain impression is recorded as the fingers it covers —
   * a flat thumb as the thumb, a slap as its four. See lib/workflows/amputations.ts for why skipping them
   * was wrong once a workflow could collect the flat thumbs alone.
   */
  for (const { fgp, code } of ampEntries(ampCodes)) {
    t2.fields[`84.${amp}.1`] = padNum(fgp, 2);
    t2.fields[`84.${amp}.2`] = code;
    amp++;
  }
}

/** Build the CustomEftDto for a live-capture workflow from its (server-configured) WorkflowConfig. */
/**
 * The TCN the built transaction carries, read back out of the DTO.
 *
 * The record and the file have to agree, and the TCN is RENDERED at build time — `{datetime}` and
 * `{rand:5}` make every call produce a different value — so it cannot be recomputed later to find out what
 * a file says. Every ATF transaction was recorded as `EFTC-<uuid>` because the server invented a
 * placeholder when the client sent no TCN, and the client had none to send: it posted before it built.
 *
 * Reading it off the DTO is what lets one build serve both, which is the only way they cannot drift.
 */
export function tcnOfDto(dto: CustomEftDto): string | undefined {
  const t1 = dto.transaction.records.find((r) => r.type === 'TYPE_01');
  const v = t1?.fields['9'];
  const tcn = v == null ? '' : String(v).trim();
  return tcn || undefined;
}

export function buildCustomEft(
  cfg: WorkflowConfig,
  demo: Demo,
  prints: Record<string, string>,
  ampCodes: Record<string, string>,
  device: DeviceInfo | null,
  /** Base64 PNG from the face step. Only reaches the EFT when the workflow targets `type10`. */
  facePhoto?: string | null,
): CustomEftDto {
  const d: Demo = { ...demo };
  if (d['ssn']) d['ssn'] = d['ssn'].replace(/-+/g, '');
  /*
   * Every DATE the workflow defines, written in the shape its field expects.
   *
   * This used to normalise `dateOfBirth` and nothing else, so any other mapped date went out exactly as
   * the date input produced it — `datePrinted` reaches 2.038 (DPR) on both card-conversion workflows and
   * was being written as "2026-08-24" into a field specified as CCYYMMDD. Driving it from the field
   * definitions fixes every such field at once, and lets a workflow name a different format where the
   * specification asks for one.
   *
   * `formatDateValue` is string surgery on purpose — see the timezone note there.
   */
  const dateFormats = new Map(
    (cfg.fields ?? [])
      .filter((f) => f.type === 'date')
      .map((f) => [f.name, f.dateFormat ?? DEFAULT_DATE_FORMAT] as const),
  );
  for (const [key, val] of Object.entries(d)) {
    if (!val) continue;
    const fmt = dateFormats.get(key);
    if (fmt) {
      d[key] = formatDateValue(val, fmt);
      continue;
    }
    /*
     * No field definition to consult — an imported EFT, a resumed draft, or a value a post-rule produced.
     * An ISO-shaped date still has to reach the EBTS as CCYYMMDD, and the pattern is strict enough that
     * nothing else can match it: a serial number or a case reference is left exactly as typed.
     */
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) d[key] = formatDateValue(val, DEFAULT_DATE_FORMAT);
  }

  /*
   * Seed the capture-equipment demographics from the connected scanner.
   *
   * SWFT's fieldMap already routes imaMak/imaModl/imaSerno to Type-2 67.1.1/67.1.2/67.1.3, but nothing
   * ever populated them — there are no form fields for them and no rule filled them in — so every SWFT
   * transaction went out without the make, model and serial number v1 recorded.
   *
   * This seeds `demo` rather than writing t2.fields directly (which is what deviceCaptureFields does for
   * DOCE) because SWFT's TCN template contains {field:imaSerno}. Writing the Type-2 field after the fact
   * would fix the EBTS field and still leave the TCN missing its serial segment — the TCN is rendered
   * from the demographics, not from the built record.
   *
   * Operator-entered values win; this only fills blanks.
   */
  /*
   * The connected scanner, always available to rules and to the TCN template as hidden form data.
   *
   * Rules read form fields; the scanner is not one. Rather than give the engine a second kind of source, the
   * device is seeded into the form values under reserved names — so a rule writes 2.067.1.3 by reading
   * `device_serial` exactly as it reads any other field, and `{field:device_serial}` works in a TCN template
   * for free. One mechanism instead of two.
   *
   * Reserved, and overwritten rather than only filling blanks: these describe the hardware that took the
   * impression, so a stale value carried in from a draft would be a false record of it. A workflow that wants
   * the operator to be able to correct a bad reading uses `deviceDemoFields` below, which does fill blanks
   * only and writes to its own field names.
   */
  if (device) {
    if (device.vendorName) d['device_make'] = device.vendorName;
    if (device.deviceName) d['device_model'] = device.deviceName;
    if (device.deviceSerial) d['device_serial'] = device.deviceSerial;
  }

  const t1: AniRecord = { type: 'TYPE_01', idx: 1, fields: {} };
  const t2: AniRecord = { type: 'TYPE_02', idx: 1, fields: {} };
  const records: AniRecord[] = [t1, t2];

  t1.fields['5'] = todayYmd(); // T1_DAT — local calendar date, no timezone shift
  Object.assign(t1.fields, cfg.staticType1Fields ?? {});
  Object.assign(t2.fields, cfg.staticType2Fields ?? {});

  /*
   * Mapped demographics go to the record the mapping names — Type-1 as well as Type-2. A reference that
   * cannot be parsed is SKIPPED rather than guessed at: writing a demographic into an unintended field is
   * worse than leaving it out, since the FBI rejects a bad value as readily as a missing one and the
   * rejection names the wrong field.
   */
  for (const [key, val] of Object.entries(d)) {
    const raw = cfg.fieldMap?.[key];
    if (!val || !raw) continue;
    const ref = tryParseEbtsRef(raw);
    if (!ref) { console.error(`Workflow ${cfg.captureType}: fieldMap "${key}" -> "${raw}" is not a field reference; skipped`); continue; }
    const target = ref.record === 1 ? t1 : ref.record === 2 ? t2 : undefined;
    if (!target) { console.error(`Workflow ${cfg.captureType}: fieldMap "${key}" targets Type-${ref.record}, which this builder does not create; skipped`); continue; }
    target.fields[ref.key] = val;
  }

  addFingerprints(records, t2, prints, ampCodes);

  // TCN
  const ori = t1.fields['8'] ? String(t1.fields['8']) : cfg.ori;
  /*
   * The photograph, only when the workflow puts it in the EFT. Added after the ORI is resolved because
   * the Type-10 records the capturing agency, and after the fingerprints so the content list keeps the
   * conventional record order.
   */
  if (cfg.face && facePhoto) addFace(records, cfg.face, facePhoto, ori, String(t1.fields['5'] ?? todayYmd()));
  t1.fields['9'] = renderTcn(cfg.tcnTemplate || '{ori}-{datetime}-EFTC-{rand:5}', { ori, tot: cfg.tot, spec: cfg.specification, demo: d });

  /*
   * There are no hardcoded post-rules any more.
   *
   * setName, dprFromDat, criFromOri, crlfFields, deviceCaptureFields and deviceDemoFields were a second way
   * to do what a rule describes, and the builder had to run them in a fixed order around the configurable
   * ones. Each is now expressed as rules by `postRulesToRules`, which a stored configuration gets folded
   * into it when it loads — so a workflow that was never re-saved behaves exactly as before and this file
   * has one mechanism instead of two.
   */
  /*
   * Configurable rules, LAST — so a rule can read anything the hardcoded ones produced (the composed name,
   * the copied ORI) and correct or extend it, rather than racing them.
   *
   * The rules see and write EBTS values by qualified reference; those are folded into the records afterwards,
   * so a rule cannot reach past the two records this builder creates.
   */
  if (cfg.rules?.length) {
    /*
     * Keyed the way a rule spells a reference, which is NOT the same as padding the record's own key.
     *
     * A record keys a positioned field `73.2.1`; padding that whole string leaves it alone (it is already
     * longer than three characters) and produces `2.73.2.1`, which no rule would ever be written against —
     * the editor stores `2.073.2`. A rule reading a subfield therefore saw nothing at all. Rebuilding the
     * reference from the parsed coordinates gives the spelling the rule actually uses.
     */
    const seen: Record<string, string> = {};
    const seedKey = (record: number, key: string): string => {
      const p = tryParseEbtsRef(key);
      return p ? formatEbtsRef({ record, field: p.field, subfield: p.subfield, item: p.item })
               : `${record}.${String(key).padStart(3, '0')}`;
    };
    for (const [k, v] of Object.entries(t1.fields)) seen[seedKey(1, String(k))] = String(v);
    for (const [k, v] of Object.entries(t2.fields)) seen[seedKey(2, String(k))] = String(v);
    const produced = runRules(cfg.rules, d, seen);
    for (const [ref, value] of Object.entries(produced)) {
      const parsed = tryParseEbtsRef(ref);
      const target = parsed?.record === 1 ? t1 : parsed?.record === 2 ? t2 : undefined;
      if (!parsed || !target) { console.error(`Workflow ${cfg.captureType}: rule wrote "${ref}", which this builder cannot reach`); continue; }
      /*
       * An empty result is NOT written. An empty Type-2 field is a violation in its own right — the FBI
       * rejects `2.017:` exactly as it rejects a bad value — so a rule that produces nothing leaves the field
       * absent rather than present-and-blank.
       */
      if (!String(value).trim()) continue;
      target.fields[parsed.key] = value;
    }
  }

  /*
   * Drop every empty field before the DTO leaves.
   *
   * An empty Type-2 field is an EBTS violation, not an absent value — the FBI rejects the submission — and there
   * are several ways one can reach here even though the demographic loop above skips blanks: dprFromDat and
   * criFromOri fall back to '', the device fields write whatever the scanner reported (which can be an empty
   * name or serial), and a site admin can save a static field with an empty value. One sweep at the exit is
   * worth more than a guard at each of those, because it also covers the next one somebody adds.
   */
  stripEmptyFields(records);

  return { transaction: { idcPadded: Boolean(cfg.zeroPaddedIdc), records } };
}

/** Remove field entries whose value is empty or whitespace-only. Numbers and images are always kept. */
export function stripEmptyFields(records: AniRecord[]): void {
  for (const record of records) {
    for (const [key, value] of Object.entries(record.fields)) {
      if (typeof value === 'string' && value.trim() === '') delete record.fields[key];
    }
  }
}
